import { useState } from 'react';
import { FlaskConical, Sparkles, Loader2 } from 'lucide-react';
import { useConfigStore } from '@/store/useConfigStore';
import { optimizeTitle } from '@/services/llmService';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select, Switch } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { DEFAULT_TITLE_CONFIG } from '@/lib/constants';
import type { LLMInstance } from '@/types';

export function TitleOptimizationPage() {
    const { titleConfig, updateTitleConfig, llmInstances } = useConfigStore();

    const [testOpen, setTestOpen] = useState(false);

    const selectedInstance = llmInstances.find(i => i.id === titleConfig.llmInstanceId) ?? null;

    const canTest = !!selectedInstance && !!titleConfig.model && !!titleConfig.prompt.trim();

    const handleInstanceChange = (id: string) => {
        const inst = llmInstances.find(i => i.id === id);
        const firstModel = inst?.models[0] ?? '';
        updateTitleConfig({ llmInstanceId: id, model: firstModel });
    };

    return (
        <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">标题优化</h1>
                    <p className="text-xs text-zinc-500">基于 LLM 优化产品名称，提升搜索曝光</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTestOpen(true)}
                    disabled={!canTest}
                >
                    <FlaskConical size={14} />
                    测试
                </Button>
            </div>

            <div className="flex-1 overflow-auto p-4">
                <div className="mx-auto flex max-w-3xl flex-col gap-4">
                    {/* 配置面板 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-zinc-900">配置</h2>
                                <p className="text-xs text-zinc-500">开启后可通过批量优化处理产品名称列</p>
                            </div>
                            <Switch
                                checked={titleConfig.enabled}
                                onChange={(v) => updateTitleConfig({ enabled: v })}
                                label={titleConfig.enabled ? '已启用' : '已禁用'}
                            />
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Select
                                label="LLM 实例"
                                value={titleConfig.llmInstanceId}
                                onChange={(e) => handleInstanceChange(e.target.value)}
                                disabled={!titleConfig.enabled}
                            >
                                <option value="">请选择实例</option>
                                {llmInstances.map(inst => (
                                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                                ))}
                            </Select>
                            <Select
                                label="模型"
                                value={titleConfig.model}
                                onChange={(e) => updateTitleConfig({ model: e.target.value })}
                                disabled={!titleConfig.enabled || !selectedInstance}
                            >
                                <option value="">请选择模型</option>
                                {selectedInstance && selectedInstance.models.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </Select>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Select label="提交方式" value={titleConfig.submissionMode} onChange={(e) => updateTitleConfig({ submissionMode: e.target.value as typeof titleConfig.submissionMode })} disabled={!titleConfig.enabled}>
                                <option value="single">逐商品请求</option>
                                <option value="json">批量 JSON</option>
                                <option value="csv">批量 CSV</option>
                            </Select>
                            <Input label="每批商品数" type="number" min={1} max={100} value={titleConfig.batchProductCount} onChange={(e) => updateTitleConfig({ batchProductCount: Math.max(1, Math.min(100, Number(e.target.value) || 10)) })} disabled={!titleConfig.enabled || titleConfig.submissionMode === 'single'} />
                        </div>

                        <div className="mt-3">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-zinc-600">请求间隔</label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={titleConfig.requestInterval}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            updateTitleConfig({ requestInterval: v >= 0 ? v : 0 });
                                        }}
                                        disabled={!titleConfig.enabled}
                                        className="w-20 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-400"
                                    />
                                    <span className="text-xs text-zinc-400">秒</span>
                                </div>
                                <p className="text-xs text-zinc-400">批量优化时每次请求之间的间隔时间</p>
                            </div>
                        </div>

                        <div className="mt-3">
                            <Textarea
                                label="提示词"
                                value={titleConfig.prompt}
                                onChange={(e) => updateTitleConfig({ prompt: e.target.value })}
                                placeholder={DEFAULT_TITLE_CONFIG.prompt}
                                className="min-h-[160px]"
                                disabled={!titleConfig.enabled}
                            />
                            <div className="mt-1 flex items-center justify-between">
                                <p className="text-xs text-zinc-400">自定义提示词，控制优化行为</p>
                                <button
                                    onClick={() => updateTitleConfig({ prompt: DEFAULT_TITLE_CONFIG.prompt })}
                                    className="text-xs text-zinc-500 hover:text-zinc-700 hover:underline disabled:text-zinc-300 disabled:no-underline"
                                    disabled={!titleConfig.enabled}
                                >
                                    恢复默认
                                </button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <TestModal
                open={testOpen}
                onClose={() => setTestOpen(false)}
                instance={selectedInstance}
                model={titleConfig.model}
                prompt={titleConfig.prompt}
            />
        </div>
    );
}

function TestModal({
    open,
    onClose,
    instance,
    model,
    prompt,
}: {
    open: boolean;
    onClose: () => void;
    instance: LLMInstance | null;
    model: string;
    prompt: string;
}) {
    const [input, setInput] = useState('');
    const [result, setResult] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleOptimize = async () => {
        if (!instance || !model || !input.trim()) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const out = await optimizeTitle(instance, model, prompt, input.trim());
            setResult(out);
        } catch (e) {
            setError(e instanceof Error ? e.message : '优化失败');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setInput('');
        setResult(null);
        setError(null);
        setLoading(false);
        onClose();
    };

    const showResult = loading || result !== null || error !== null;

    return (
        <Modal open={open} onClose={handleClose} title="标题优化测试" width="lg">
            <div className="flex flex-col gap-4">
                <Textarea
                    label="商品标题"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="输入要测试的商品标题"
                    className="min-h-[100px]"
                />
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleOptimize}
                        disabled={loading || !input.trim() || !instance || !model}
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        优化
                    </Button>
                </div>

                {showResult && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                            <p className="mb-2 text-xs font-medium text-zinc-500">优化前</p>
                            <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
                                {input || '—'}
                            </p>
                        </div>
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                            <p className="mb-2 text-xs font-medium text-zinc-500">优化后</p>
                            {loading ? (
                                <div className="flex items-center gap-2 text-sm text-zinc-400">
                                    <Loader2 size={14} className="animate-spin" />
                                    优化中...
                                </div>
                            ) : error ? (
                                <p className="whitespace-pre-wrap break-words text-sm text-red-500">{error}</p>
                            ) : (
                                <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
                                    {result ?? '—'}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {result && (
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setResult(null);
                                setError(null);
                            }}
                        >
                            清除结果
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
}
